import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Users,
  Mail,
  Mic,
  Save,
  CheckCircle2,
  AlertCircle,
  Send,
  TestTube,
  Phone,
  Key,
  Globe,
  Star,
  ExternalLink,
  Settings,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Badge,
} from '@/components/ui'
import { engagementApi } from '@/api/endpoints/engagement'
import { agentPoolApi, type AgentProfile } from '@/api/endpoints/agentPool'
import { cn } from '@/lib/utils'

// Language flag mapping
const getLanguageFlag = (code: string) => {
  const flags: Record<string, string> = {
    en: '🇺🇸',
    es: '🇪🇸',
    fr: '🇫🇷',
    pt: '🇧🇷',
    de: '🇩🇪',
    it: '🇮🇹',
    zh: '🇨🇳',
    ja: '🇯🇵',
    ko: '🇰🇷',
  }
  return flags[code] || '🌐'
}

export default function Engagement() {
  const queryClient = useQueryClient()
  const [testEmail, setTestEmail] = useState('')
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null)
  const [testEmailSuccess, setTestEmailSuccess] = useState<string | null>(null)
  const [testEmailError, setTestEmailError] = useState<string | null>(null)

  // BYOT state
  const [byotEncryptedKey, setByotEncryptedKey] = useState('')
  const [byotCallerId, setByotCallerId] = useState('')
  const [byotSuccess, setByotSuccess] = useState<string | null>(null)
  const [byotError, setByotError] = useState<string | null>(null)

  // Fetch agent pool data
  const { data: poolsData, isLoading: poolsLoading } = useQuery({
    queryKey: ['agent-pools'],
    queryFn: () => agentPoolApi.getPools(true).then(res => res.data),
    staleTime: 30000,
  })

  // Fetch BYOT config
  const { data: byotData } = useQuery({
    queryKey: ['byotConfig'],
    queryFn: () => engagementApi.getBYOTConfig(),
  })

  // Update BYOT caller ID when data loads
  useEffect(() => {
    if (byotData?.data?.outbound_caller_id) {
      setByotCallerId(byotData.data.outbound_caller_id)
    }
  }, [byotData])

  // Test email mutation
  const testEmailMutation = useMutation({
    mutationFn: (params: { toEmail: string; agentId?: number }) =>
      engagementApi.sendTestEmail({
        to_email: params.toEmail,
        agent_id: params.agentId,
      }),
    onSuccess: (response) => {
      const langInfo = response.data.language && response.data.language !== 'en'
        ? ` (in ${response.data.language.toUpperCase()})`
        : ''
      setTestEmailSuccess(`Test email sent successfully to ${response.data.to_email}${langInfo}`)
      setTestEmailError(null)
      setTimeout(() => setTestEmailSuccess(null), 5000)
    },
    onError: (error: Error) => {
      setTestEmailError(error.message || 'Failed to send test email')
      setTestEmailSuccess(null)
    },
  })

  // BYOT update mutation
  const byotMutation = useMutation({
    mutationFn: (config: { encrypted_key?: string; outbound_caller_id?: string }) =>
      engagementApi.updateBYOTConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['byotConfig'] })
      setByotSuccess('BYOT configuration saved successfully')
      setByotError(null)
      setByotEncryptedKey('') // Clear the key field after saving
      setTimeout(() => setByotSuccess(null), 5000)
    },
    onError: (error: Error) => {
      setByotError(error.message || 'Failed to save BYOT configuration')
      setByotSuccess(null)
    },
  })

  const handleSendTestEmail = () => {
    if (!testEmail || !testEmail.includes('@')) {
      setTestEmailError('Please enter a valid email address')
      return
    }
    setTestEmailError(null)
    testEmailMutation.mutate({
      toEmail: testEmail,
      agentId: selectedAgentId || undefined,
    })
  }

  const handleSaveBYOT = () => {
    const config: { encrypted_key?: string; outbound_caller_id?: string } = {}

    // Only include encrypted_key if user entered a new one
    if (byotEncryptedKey.trim()) {
      config.encrypted_key = byotEncryptedKey.trim()
    }

    // Always include caller ID if set
    if (byotCallerId.trim()) {
      config.outbound_caller_id = byotCallerId.trim()
    }

    if (!config.encrypted_key && !config.outbound_caller_id) {
      setByotError('Please enter at least an encrypted key or caller ID')
      return
    }

    setByotError(null)
    byotMutation.mutate(config)
  }

  // Get agents from pool
  const activePool = poolsData?.pools?.find(p => p.is_active)
  const agents = activePool?.agents || []
  const primaryAgent = agents.find(a => a.is_primary)
  const outboundDefaultAgent = agents.find(a => a.is_outbound_default)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Engagement Configuration</h1>
          <p className="text-muted-foreground">
            Configure AI agents and communication settings for outbound engagement
          </p>
        </div>
      </div>

      {/* Agent Pool Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                AI Agent Pool
              </CardTitle>
              <CardDescription>
                {activePool ? activePool.name : 'No active pool configured'}
              </CardDescription>
            </div>
            <Link to="/agent-pool">
              <Button variant="outline">
                <Settings className="h-4 w-4 mr-2" />
                Manage Agents
                <ExternalLink className="h-3 w-3 ml-2" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {poolsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : agents.length === 0 ? (
            <div className="text-center py-8 border rounded-lg bg-muted/30">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No agents configured</h3>
              <p className="text-muted-foreground mb-4">
                Set up AI agents to handle outbound calls in different languages
              </p>
              <Link to="/agent-pool">
                <Button>
                  <Settings className="h-4 w-4 mr-2" />
                  Configure Agent Pool
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Pool Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Total Agents</span>
                  </div>
                  <p className="text-2xl font-bold">{agents.length}</p>
                </div>
                <div className="p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Active</span>
                  </div>
                  <p className="text-2xl font-bold">{agents.filter(a => a.is_active).length}</p>
                </div>
                <div className="p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Languages</span>
                  </div>
                  <p className="text-2xl font-bold">
                    {new Set(agents.map(a => a.primary_language)).size}
                  </p>
                </div>
              </div>

              {/* Agents List */}
              <div className="border rounded-lg divide-y">
                {agents.map((agent: AgentProfile) => (
                  <div
                    key={agent.id}
                    className={cn(
                      "p-4 flex items-center justify-between",
                      !agent.is_active && "opacity-50 bg-muted/20"
                    )}
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className={cn(
                        "h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold text-lg",
                        agent.gender === 'female' ? 'bg-pink-500' :
                        agent.gender === 'male' ? 'bg-blue-500' : 'bg-gray-500'
                      )}>
                        {agent.name.charAt(0)}
                      </div>

                      {/* Agent Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{agent.name}</span>
                          {agent.is_primary && (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <Star className="h-3 w-3 mr-1" />
                              Primary
                            </Badge>
                          )}
                          {agent.is_outbound_default && (
                            <Badge className="bg-blue-100 text-blue-800">
                              Outbound Default
                            </Badge>
                          )}
                          {!agent.is_active && (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {agent.title} at {agent.company}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <span className="text-base">{getLanguageFlag(agent.primary_language)}</span>
                            {agent.primary_language_name || agent.primary_language.toUpperCase()}
                          </span>
                          {agent.voice_name && (
                            <span className="flex items-center gap-1">
                              <Mic className="h-3 w-3" />
                              {agent.voice_name}
                            </span>
                          )}
                          {agent.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {agent.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="text-right text-sm">
                      <p className="font-medium">{agent.total_calls} calls</p>
                      <p className="text-muted-foreground">
                        {agent.successful_transfers_received + agent.successful_transfers_sent} transfers
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pool Settings Summary */}
              {activePool && (
                <div className="flex items-center gap-4 text-sm text-muted-foreground pt-2 border-t">
                  <span className="flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    Language Detection: {activePool.language_detection_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <span>|</span>
                  <span>
                    Auto Transfer: {activePool.auto_transfer_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Email */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="h-5 w-5" />
            Test Email Configuration
          </CardTitle>
          <CardDescription>
            Send a test email to verify the O365 mailbox configuration is working correctly.
            Select an agent to test language-specific email content.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Success Message */}
          {testEmailSuccess && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-green-200 bg-green-50 text-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>{testEmailSuccess}</span>
            </div>
          )}

          {/* Error Message */}
          {testEmailError && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="h-4 w-4" />
              <span>{testEmailError}</span>
            </div>
          )}

          {/* Agent Selection */}
          {agents.length > 0 && (
            <div className="space-y-2">
              <Label>Send as Agent</Label>
              <div className="flex flex-wrap gap-2">
                {agents.filter(a => a.is_active).map((agent) => (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={() => setSelectedAgentId(selectedAgentId === agent.id ? null : agent.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all",
                      selectedAgentId === agent.id
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-muted hover:border-primary/50 hover:bg-muted/50"
                    )}
                  >
                    <span className="text-base">{getLanguageFlag(agent.primary_language)}</span>
                    <span className="font-medium">{agent.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({agent.primary_language_name || agent.primary_language.toUpperCase()})
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedAgentId
                  ? `Email will be sent in ${agents.find(a => a.id === selectedAgentId)?.primary_language_name || 'the selected language'}`
                  : 'No agent selected - email will be sent in English from the default persona'}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="test_email" className="sr-only">
                Recipient Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="test_email"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter recipient email address"
                  className="pl-10"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSendTestEmail()
                    }
                  }}
                />
              </div>
            </div>
            <Button
              onClick={handleSendTestEmail}
              disabled={testEmailMutation.isPending || !testEmail}
            >
              {testEmailMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test Email
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            {selectedAgentId ? (
              <>
                The test email will be sent from{' '}
                <strong>{agents.find(a => a.id === selectedAgentId)?.email || 'the selected agent'}</strong>{' '}
                in <strong>{agents.find(a => a.id === selectedAgentId)?.primary_language_name || 'their language'}</strong>.
              </>
            ) : (
              <>
                The test email will be sent from{' '}
                <strong>{primaryAgent?.email || outboundDefaultAgent?.email || 'the primary agent'}</strong>{' '}
                using the default persona configuration.
              </>
            )}
            {' '}Check the recipient's inbox (and spam folder) to verify delivery.
          </p>
        </CardContent>
      </Card>

      {/* BYOT (Bring Your Own Twilio) Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            BYOT Configuration
          </CardTitle>
          <CardDescription>
            Configure Bring Your Own Twilio (BYOT) settings for outbound calls with your own phone numbers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Success Message */}
          {byotSuccess && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-green-200 bg-green-50 text-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>{byotSuccess}</span>
            </div>
          )}

          {/* Error Message */}
          {byotError && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="h-4 w-4" />
              <span>{byotError}</span>
            </div>
          )}

          {/* Current Status */}
          {byotData?.data && (
            <div className={`p-3 rounded-lg border ${byotData.data.is_configured ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className="flex items-center gap-2">
                {byotData.data.is_configured ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-green-800 font-medium">BYOT is configured</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <span className="text-amber-800 font-medium">BYOT is not configured</span>
                  </>
                )}
              </div>
              {byotData.data.is_configured && (
                <div className="mt-2 text-sm text-muted-foreground">
                  {byotData.data.outbound_caller_id && (
                    <div>Outbound Caller ID: <strong>{byotData.data.outbound_caller_id}</strong></div>
                  )}
                  {byotData.data.encrypted_key_masked && (
                    <div>Encrypted Key: <code className="bg-muted px-1 rounded">{byotData.data.encrypted_key_masked}</code></div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Encrypted Key */}
            <div className="space-y-2">
              <Label htmlFor="byot_encrypted_key">Encrypted Key</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="byot_encrypted_key"
                  type="password"
                  value={byotEncryptedKey}
                  onChange={(e) => setByotEncryptedKey(e.target.value)}
                  placeholder={byotData?.data?.is_configured ? 'Enter new key to update' : 'Enter encrypted key from Bland.ai'}
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Generate this in your Bland.ai dashboard under BYOT settings
              </p>
            </div>

            {/* Outbound Caller ID */}
            <div className="space-y-2">
              <Label htmlFor="byot_caller_id">Outbound Caller ID</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="byot_caller_id"
                  type="tel"
                  value={byotCallerId}
                  onChange={(e) => setByotCallerId(e.target.value)}
                  placeholder="+18889909808"
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Your Twilio phone number in E.164 format (e.g., +18889909808)
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSaveBYOT}
              disabled={byotMutation.isPending || (!byotEncryptedKey.trim() && !byotCallerId.trim())}
            >
              {byotMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save BYOT Configuration
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground border-t pt-3">
            BYOT allows you to use your own Twilio phone numbers for outbound calls.
            This enables caller ID lookup and ensures callbacks reach your AI agent.
            You must first configure BYOT in your Bland.ai dashboard and generate an encrypted key.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
